import { ColumnSelection, PrintColumnSelectionMode } from '../models/dialog.model';

/**
 * Identity and labelling for a printable column. Shared so the Select Columns dialog and the
 * print preview bar's inline panel key and name a column identically — they list the same
 * columns in different shapes, and a key that disagrees silently loses a selection.
 */

/** Columns predate `columnKey`, so several callers still identify them by display name. */
export function getPrintColumnKey(column: ColumnSelection): string {
  return column.columnKey ?? column.columnName;
}

/** Older column sets carry only a composed `columnName` — "Assay 42" — with the region on the end. */
export function getPrintColumnAssayName(column: ColumnSelection): string {
  if (column.assayName?.trim()) {
    return column.assayName;
  }
  const fallback = column.columnName ?? '';
  const match = fallback.match(/^(.*)\s+(-?\d+(?:\.\d+)?)$/);
  return match ? match[1].trim() : fallback;
}

export function getPrintColumnBeadRegion(column: ColumnSelection): string | number {
  if (column.beadRegion !== undefined && column.beadRegion !== null && `${column.beadRegion}`.trim().length) {
    return column.beadRegion;
  }
  const fallback = column.columnName ?? '';
  const match = fallback.match(/(-?\d+(?:\.\d+)?)$/);
  return match ? match[1] : '-';
}

/** The identifying fields of one column, in the order the given mode lists them. */
export function getPrintColumnParts(
  column: ColumnSelection,
  mode: PrintColumnSelectionMode
): (string | number)[] {
  switch (mode) {
    case 'assayBead':
      return [getPrintColumnAssayName(column), getPrintColumnBeadRegion(column)];
    case 'assayInstrumentBead':
      return [getPrintColumnAssayName(column), column.instrumentId ?? '-', getPrintColumnBeadRegion(column)];
    default:
      return [column.kitLot ?? column.columnName, column.instrumentId ?? '-', column.beadRegion ?? '-'];
  }
}

/** The same fields as one line, for the inline panel where a table would be too heavy. */
export function getPrintColumnLabel(column: ColumnSelection, mode: PrintColumnSelectionMode): string {
  return getPrintColumnParts(column, mode).join(' · ');
}

/**
 * A print selection re-keyed onto a freshly built column set.
 *
 * Print screens rebuild `tableColumns` from the store with everything selected. Without this the
 * operator's choice is dropped on the next rebuild — which a print itself can trigger, via the
 * audit write — and the whole table goes to the printer however few columns were ticked.
 */
export function applyPrintColumnSelection(
  columns: ColumnSelection[],
  selection: ColumnSelection[] | null | undefined
): ColumnSelection[] {
  if (!selection) {
    return columns;
  }
  const chosen = new Set(
    selection.filter(column => column.isSelected).map(column => getPrintColumnKey(column))
  );
  return columns.map(column => ({
    ...column,
    isSelected: chosen.has(getPrintColumnKey(column))
  }));
}

/**
 * The usable width of a printed page, in millimetres.
 *
 * `@page { margin: 8mm 15mm 0mm 8mm }` in styles.scss spends 23mm horizontally, which leaves 187mm
 * on A4 and 192.9mm on US Letter. 185 is safe on both with a little slack, and the app does not
 * set `@page { size: … }`, so either may be what comes out of the printer.
 */
export const PRINTABLE_WIDTH_MM = 185;

/**
 * How much page width one report table needs per column.
 *
 * Screens used to declare a `columnsPerPrintPage` count instead — 2, 7, 8 and 9 across eight
 * components, none of them derived from anything. Nine 120px columns plus a label column is far
 * wider than a portrait page, so the browser was left to shrink the page to fit and the printout
 * came out smaller than it had to be. Declaring the widths instead makes the count fall out of the
 * page, and a screen whose columns are genuinely wide (Sample Errors carries error messages) gets
 * the right answer from the same helper.
 */
export interface PrintPageFit {
  /** The pinned identity column, repeated on every chunk page. */
  labelWidthMm: number;
  /** One data column at print typography. */
  columnWidthMm: number;
  /** Defaults to `PRINTABLE_WIDTH_MM`. */
  printableWidthMm?: number;
}

/** How many data columns fit on one printed page beside the label column. Never less than one. */
export function columnsPerPrintPage(fit: PrintPageFit): number {
  const printable = fit.printableWidthMm ?? PRINTABLE_WIDTH_MM;
  const available = printable - fit.labelWidthMm;
  if (fit.columnWidthMm <= 0) {
    return 1;
  }
  return Math.max(1, Math.floor(available / fit.columnWidthMm));
}

/**
 * A wide column set split into page-sized chunks.
 *
 * Every column lands in exactly one chunk — these are QC records, so a chunk boundary may add a
 * page but must never drop a column.
 */
export function chunkColumnsForPrint<T>(columns: T[] | null | undefined, fit: PrintPageFit): T[][] {
  if (!columns?.length) {
    return [];
  }
  const perPage = columnsPerPrintPage(fit);
  const pages: T[][] = [];
  for (let index = 0; index < columns.length; index += perPage) {
    pages.push(columns.slice(index, index + perPage));
  }
  return pages;
}

/**
 * Which slice of the report one chunk page holds — "Columns 10-18 of 22 - page 2 of 3".
 *
 * A continuation page is otherwise unlabelled numbers under a repeated row-label column, with
 * nothing saying where it sits in the report or whether a page is missing from the record.
 */
export function describePrintPage(pageIndex: number, pages: unknown[][]): string {
  if (pages.length <= 1) {
    return '';
  }
  const total = pages.reduce((sum, page) => sum + page.length, 0);
  const first = pages.slice(0, pageIndex).reduce((sum, page) => sum + page.length, 0) + 1;
  const last = first + (pages[pageIndex]?.length ?? 0) - 1;
  const columns = first === last ? `Column ${first} of ${total}` : `Columns ${first}–${last} of ${total}`;
  return `${columns} · page ${pageIndex + 1} of ${pages.length}`;
}

/** The column headings matching `getPrintColumnParts` for the given mode. */
export function getPrintColumnHeadings(mode: PrintColumnSelectionMode): string[] {
  switch (mode) {
    case 'assayBead':
      return ['Assay Name', 'Bead Region'];
    case 'assayInstrumentBead':
      return ['Assay Name', 'Instrument ID', 'Bead Region'];
    default:
      return ['Kit Lot', 'Instrument ID', 'Bead Region'];
  }
}
